import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Mail, Phone, Calendar, Shield, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';

const PlayerRegistration = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    email: '',
    full_name: '',
    phone_number: '',
    date_of_birth: '',
    position_preference: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    medical_notes: '',
    previous_club: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const clubLogo = "https://customer-assets.emergentagent.com/job_cafc-platform/artifacts/d9rcitex_OurLogo.png";

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    try {
      const registrationData = { ...formData };
      delete registrationData.confirmPassword; // Remove confirm password field

      await axios.post('/players/register', registrationData);
      setSuccess(true);
    } catch (error) {
      setError(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <img 
              src={clubLogo} 
              alt="Crawley Athletic FC" 
              className="h-16 w-16"
            />
          </div>
          <div className="bg-white rounded-lg shadow p-8 mt-8">
            <div className="text-center">
              <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Registration Successful!</h2>
              <p className="text-gray-600 mb-6">
                Your player account has been created successfully. You can now log in to access your player portal.
              </p>
              <div className="space-y-3">
                <p className="text-sm text-gray-500">
                  <strong>What happens next:</strong>
                </p>
                <ul className="text-sm text-gray-500 space-y-1">
                  <li>• Club staff will review your registration</li>
                  <li>• You'll be contacted about training sessions</li>
                  <li>• Access your player portal with your login credentials</li>
                </ul>
              </div>
              <div className="mt-8">
                <Link to="/login" className="btn btn-primary">
                  Sign In to Player Portal
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-2xl">
        <div className="flex justify-center">
          <img 
            src={clubLogo} 
            alt="Crawley Athletic FC" 
            className="h-16 w-16"
          />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          Join Crawley Athletic
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Register as a player to access your personal portal
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-2xl">
        <div className="bg-white py-8 px-6 shadow rounded-lg">
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Account Information */}
              <div className="md:col-span-2">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h3>
              </div>
              
              <div className="form-group">
                <label htmlFor="username" className="form-label">
                  <User className="h-4 w-4 inline mr-2" />
                  Username *
                </label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  required
                  className="form-input"
                  value={formData.username}
                  onChange={handleInputChange}
                  placeholder="Choose a unique username"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  <Mail className="h-4 w-4 inline mr-2" />
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="form-input"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your.email@example.co.uk"
                />
              </div>

              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  <Shield className="h-4 w-4 inline mr-2" />
                  Password *
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  required
                  className="form-input"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="At least 6 characters"
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword" className="form-label">
                  <Shield className="h-4 w-4 inline mr-2" />
                  Confirm Password *
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  required
                  className="form-input"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Repeat your password"
                />
              </div>

              {/* Personal Information */}
              <div className="md:col-span-2">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 mt-6">Personal Information</h3>
              </div>

              <div className="form-group">
                <label htmlFor="full_name" className="form-label">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="full_name"
                  name="full_name"
                  required
                  className="form-input"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  placeholder="Your full name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone_number" className="form-label">
                  <Phone className="h-4 w-4 inline mr-2" />
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone_number"
                  name="phone_number"
                  required
                  className="form-input"
                  value={formData.phone_number}
                  onChange={handleInputChange}
                  placeholder="+44 123 456 7890"
                />
              </div>

              <div className="form-group">
                <label htmlFor="date_of_birth" className="form-label">
                  <Calendar className="h-4 w-4 inline mr-2" />
                  Date of Birth *
                </label>
                <input
                  type="date"
                  id="date_of_birth"
                  name="date_of_birth"
                  required
                  className="form-input"
                  value={formData.date_of_birth}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="position_preference" className="form-label">
                  Preferred Position *
                </label>
                <select
                  id="position_preference"
                  name="position_preference"
                  required
                  className="form-input"
                  value={formData.position_preference}
                  onChange={handleInputChange}
                >
                  <option value="">Select Position</option>
                  <option value="Goalkeeper">Goalkeeper</option>
                  <option value="Defender">Defender</option>
                  <option value="Midfielder">Midfielder</option>
                  <option value="Forward">Forward</option>
                </select>
              </div>

              {/* Emergency Contact */}
              <div className="md:col-span-2">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 mt-6">Emergency Contact</h3>
              </div>

              <div className="form-group">
                <label htmlFor="emergency_contact_name" className="form-label">
                  Emergency Contact Name *
                </label>
                <input
                  type="text"
                  id="emergency_contact_name"
                  name="emergency_contact_name"
                  required
                  className="form-input"
                  value={formData.emergency_contact_name}
                  onChange={handleInputChange}
                  placeholder="Contact person name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="emergency_contact_phone" className="form-label">
                  Emergency Contact Phone *
                </label>
                <input
                  type="tel"
                  id="emergency_contact_phone"
                  name="emergency_contact_phone"
                  required
                  className="form-input"
                  value={formData.emergency_contact_phone}
                  onChange={handleInputChange}
                  placeholder="+44 123 456 7890"
                />
              </div>

              {/* Optional Information */}
              <div className="md:col-span-2">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 mt-6">Additional Information</h3>
              </div>

              <div className="form-group">
                <label htmlFor="previous_club" className="form-label">
                  Previous Club (Optional)
                </label>
                <input
                  type="text"
                  id="previous_club"
                  name="previous_club"
                  className="form-input"
                  value={formData.previous_club}
                  onChange={handleInputChange}
                  placeholder="Your previous football club"
                />
              </div>

              <div className="form-group md:col-span-2">
                <label htmlFor="medical_notes" className="form-label">
                  Medical Notes (Optional)
                </label>
                <textarea
                  id="medical_notes"
                  name="medical_notes"
                  rows={3}
                  className="form-textarea"
                  value={formData.medical_notes}
                  onChange={handleInputChange}
                  placeholder="Any medical conditions, allergies, or notes we should be aware of"
                />
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Terms & Conditions</h3>
              <div className="space-y-3 text-sm text-gray-600">
                <p>
                  By registering, you agree to:
                </p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Follow all club rules and regulations</li>
                  <li>Maintain good conduct on and off the pitch</li>
                  <li>Attend training sessions regularly</li>
                  <li>Pay membership fees and other charges on time</li>
                  <li>Respect coaches, teammates, and opponents</li>
                </ul>
                <p>
                  Your registration will be reviewed by club staff. You'll be contacted about training sessions and club activities.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                type="submit"
                disabled={loading}
                className={`btn btn-primary ${loading ? 'loading' : ''}`}
              >
                {loading ? 'Creating Account...' : 'Register as Player'}
              </button>
              
              <Link to="/login" className="btn btn-secondary">
                Already have an account? Sign In
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PlayerRegistration;